import {TranslatableString} from './TranslatableString';

export class Offer {
  id: number; // Номер
  city: TranslatableString; // Варна
  area: TranslatableString; // Владиславово
  type: TranslatableString; // Апартамент
  subType: TranslatableString; // 1-стаен
  pictures: string[];
  likeCounter: number; // Харесвания
  space: number; // Площ
  price: string; // Цена евро
  yardSpace: number;
  isAvailable: boolean;
  isSellable: boolean; // Продава се
  isRentable: boolean; // Дава се под наем
  isFurnished: boolean; // Обзаведен
  isRegulated: boolean; // В регулация
  isLuxury: boolean; // Луксозен имот
  isInComplex: boolean; // Жилищен комплекс
  yearOfBuilt: number; // Година на завършване
  floor: TranslatableString; // Етаж
  buildingType: TranslatableString; // Тип строителство
  description: TranslatableString; // Описание
  contactUserNameBg: string; // Име на лицето за контакт
  specifications: TranslatableString[];
  googleMapLink: string;
  youtubeLink: string;
  lat: number;
  lng: number;

  // Placeholder shown when the backend omits a field. Visible to staff but
  // unobtrusive to customers, and keeps the page from crashing.
  static readonly MISSING_FIELD_PLACEHOLDER = '—';

  // Safely builds a TranslatableString from a (possibly null) nested object.
  // If the object is missing, logs a warning with the offer id + field name
  // and returns a placeholder so a single bad record can't break the page.
  private static safeTranslatable(
    obj: any,
    fieldNames: [string, string, string, string],
    offerId: number,
    fieldLabel: string
  ): TranslatableString {
    if (!obj) {
      console.warn(
        `[Offer] Missing "${fieldLabel}" for offer id ${offerId} — showing placeholder.`
      );
      const p = Offer.MISSING_FIELD_PLACEHOLDER;
      return new TranslatableString(p, p, p, p);
    }
    return new TranslatableString(
      obj[fieldNames[0]],
      obj[fieldNames[1]],
      obj[fieldNames[2]],
      obj[fieldNames[3]]
    );
  }

  private static getSrcFromEmbedCode(rawCode: string): string {
    const tempStrArr = rawCode.split(' ');
    let srcStr = tempStrArr.find(subStr => subStr.startsWith('src="'));
    srcStr = srcStr.slice(5, srcStr.length - 1);
    return srcStr;
  }

  constructor(off: any) {
    this.id = off.property_id;
    this.area = Offer.safeTranslatable(
      off.property_region,
      ['name', 'name_en', 'name_bg', 'name_ru'],
      off.property_id,
      'property_region'
    );
    this.type = Offer.safeTranslatable(
      off.property_type,
      ['type', 'type_en', 'type_bg', 'type_ru'],
      off.property_id,
      'property_type'
    );
    this.subType = Offer.safeTranslatable(
      off.property_subtype,
      ['subtype', 'subtype_en', 'subtype_bg', 'subtype_ru'],
      off.property_id,
      'property_subtype'
    );
    this.space = off.property_space;
    this.price = off.property_price;
    this.isSellable = off.property_for_sale;
    this.isRentable = off.property_for_rent;
    this.pictures = [];
    this.isAvailable = !off.property_sold;
    if (off.picture) {
      this.pictures.push(
        'http://api.logos.bg' + '/media' + off.picture.property_pictures
      );
    }

    const cityObj = off.property_city;
    if (cityObj) {
      this.city = new TranslatableString(
        cityObj.name,
        cityObj.name_en,
        cityObj.name_bg,
        cityObj.name_ru
      );
    }
    if (off.property_like_counter) {
      this.likeCounter = off.property_like_counter;
    } else {
      this.likeCounter = 0;
    }

    if (off.property_yard_space) {
      this.yardSpace = off.property_yard_space;
    }
    if (off.property_furnished) {
      this.isFurnished = off.property_furnished;
    }
    if (off.property_regulated) {
      this.isRegulated = off.property_regulated;
    }
    if (off.luxury_property) {
      this.isLuxury = off.luxury_property;
    }
    if (off.property_complex) {
      this.isInComplex = off.property_complex;
    }
    if (off.property_year_built) {
      this.yearOfBuilt = off.property_year_built;
    }
    if (off.property_floor) {
      this.floor = new TranslatableString(
        off.property_floor,
        off.property_floor_en,
        off.property_floor_bg,
        off.property_floor_ru
      );
    }
    if (off.property_building_type) {
      this.buildingType = new TranslatableString(
        off.property_building_type.building_type,
        off.property_building_type.building_type_en,
        off.property_building_type.building_type_bg,
        off.property_building_type.building_type_ru
      );
    }
    if (off.property_description) {
      this.description = new TranslatableString(
        off.property_description,
        off.property_description_en,
        off.property_description_bg,
        off.property_description_ru
      );
    }
    if (off.property_contact_user) {
      this.contactUserNameBg = off.property_contact_user.name_bg;
    }
    this.specifications = [];

    if (off.property_google_link) {
      this.googleMapLink = Offer.getSrcFromEmbedCode(off.property_google_link);
    }
    if (off.youtube_link) {
      this.youtubeLink = Offer.getSrcFromEmbedCode(off.youtube_link);
    }
    if (off.property_longtitude && off.property_latitude) {
      this.lat = off.property_latitude;
      this.lng = off.property_longtitude;
    }
  }

  getTranslatedSpecificationsString(): TranslatableString {
    const result: TranslatableString = {value: '', valueEn: '', valueBg: '', valueRu: ''};
    for (let i = 0; i < this.specifications.length; i++) {
      if (i !== 0) {
        result.valueBg += ', ';
        result.valueEn += ', ';
      }
      result.valueBg += this.specifications[i].valueBg;
      result.valueEn += this.specifications[i].valueEn;
    }
    return result;
  }
}
